import axios from 'axios';
import { API_BASE } from '../config';

const AI_PROXY_URL = `${API_BASE}/api/ai/chat`;
const AI_VISION_URL = `${API_BASE}/api/ai/chat-vision`;
const DEFAULT_MODEL = 'openai/gpt-4o-mini';

const ASTROLOGER_SYSTEM_PROMPT = `You are AstroA2Z, an expert Vedic and Western astrologer with deep knowledge of:
- Zodiac signs, birth charts, and natal charts
- Planetary positions, transits, and their effects
- Horoscope reading (daily, weekly, monthly, yearly)
- Compatibility and synastry between signs
- Numerology and life path numbers
- Tarot card reading and interpretation
- Chinese zodiac and its meanings
- Vedic astrology (Jyotish) including doshas, dashas, nakshatras
- Palmistry basics and aura reading concepts

Provide detailed, insightful, and personalized astrological readings. Be mystical yet informative.
Use astrological terminology naturally. Format your responses with clear sections using bold text and line breaks.
Always be encouraging and provide constructive guidance.`;

export function isGeminiReady() {
  return true;
}

async function generateContent(prompt, language = 'en') {

  const langMap = {
    hi: { name: 'Hindi (हिन्दी)', script: 'Devanagari' },
    te: { name: 'Telugu (తెలుగు)', script: 'Telugu' },
    ta: { name: 'Tamil (தமிழ்)', script: 'Tamil' },
    kn: { name: 'Kannada (ಕನ್ನಡ)', script: 'Kannada' },
    ml: { name: 'Malayalam (മലയാളം)', script: 'Malayalam' },
  };
  const langCfg = langMap[language];

  const systemLangInstruction = langCfg
    ? `\n\nCRITICAL: You MUST respond ONLY in ${langCfg.name} language using ${langCfg.script} script. Do NOT use any English words except proper nouns like zodiac sign names.`
    : '';

  const userLangInstruction = langCfg
    ? `\n\n[IMPORTANT: Respond entirely in ${langCfg.name} using ${langCfg.script} script. No English except zodiac names.]`
    : '';

  try {
    const response = await axios.post(AI_PROXY_URL, {
      model: DEFAULT_MODEL,
      messages: [
        { role: 'system', content: ASTROLOGER_SYSTEM_PROMPT + systemLangInstruction },
        { role: 'user', content: prompt + userLangInstruction },
      ],
      temperature: 0.8,
      top_p: 0.95,
      max_tokens: 2048,
    });

    return response.data.content || '';
  } catch (err) {
    if (err.response?.status === 429) {
      throw new Error(
        'API rate limit exceeded. Please wait a moment and try again.'
      );
    }
    throw new Error(err.response?.data?.error?.message || err.message);
  }
}

// ===== API Functions =====

export async function getDailyHoroscope(sign, period = 'daily', language = 'en') {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const prompt = `Today's date is ${today}. Give a detailed ${period} horoscope for ${sign}. Include:
- Overall energy and theme for the ${period === 'daily' ? 'day' : period === 'weekly' ? 'week' : period === 'monthly' ? 'month' : 'year'}
- Love & Relationships
- Career & Finance
- Health & Wellness
- Lucky numbers, colors, and time
- A motivational cosmic message

Format it beautifully with clear sections.`;
  return generateContent(prompt, language);
}

export async function getBirthChart(name, dob, timeOfBirth, placeOfBirth, language = 'en') {
  const prompt = `Generate a detailed natal birth chart reading for:
Name: ${name}
Date of Birth: ${dob}
Time of Birth: ${timeOfBirth}
Place of Birth: ${placeOfBirth}

Include:
- Sun sign, Moon sign, and Rising/Ascendant sign analysis
- Planetary positions in signs and houses (approximate based on the date)
- Key aspects between planets
- Dominant element and modality
- Overall personality profile
- Life purpose and karmic lessons
- Strengths and challenges indicated by the chart
- Career inclinations
- Relationship patterns

Make it detailed, personal, and insightful.`;
  return generateContent(prompt, language);
}

export async function getCompatibility(sign1, sign2, name1 = '', name2 = '', language = 'en') {
  const prompt = `Provide a detailed love and relationship compatibility analysis between ${sign1}${name1 ? ` (${name1})` : ''} and ${sign2}${name2 ? ` (${name2})` : ''}.

Include:
- Overall compatibility score (out of 100)
- Emotional compatibility
- Intellectual compatibility
- Physical/romantic compatibility
- Communication style comparison
- Strengths of this pairing
- Challenges they may face
- Tips for a harmonious relationship
- Famous couples with this combination (if any)

Make it engaging and insightful.`;
  return generateContent(prompt, language);
}

export async function getNumerologyReading(name, dob, language = 'en') {
  const prompt = `Provide a comprehensive numerology reading for:
Name: ${name}
Date of Birth: ${dob}

Calculate and explain:
- Life Path Number and its deep meaning
- Destiny/Expression Number (from full name)
- Soul Urge/Heart's Desire Number
- Personality Number
- Birthday Number
- Personal Year Number for the current year
- Lucky numbers and their significance
- How these numbers interact to shape their life

Make it detailed and personally meaningful.`;
  return generateContent(prompt, language);
}

export async function getTarotReading(cards, question = '', language = 'en') {
  const cardDescriptions = cards.map(c =>
    `${c.position}: ${c.name}${c.reversed ? ' (Reversed)' : ' (Upright)'}`
  ).join('\n');

  const prompt = `Interpret this 3-card tarot spread:

${cardDescriptions}

${question ? `The querent's question: "${question}"` : 'This is a general life guidance reading.'}

For each card provide:
- Card meaning in its position (${cards[0]?.reversed ? 'reversed' : 'upright'})
- How it relates to the position (Past/Present/Future)
- Key symbols and their significance

Then provide:
- Overall reading synthesis
- Key message and guidance
- Action steps or advice

Make it mystical, insightful, and empowering.`;
  return generateContent(prompt, language);
}

export async function getChineseZodiacReading(year, animal, language = 'en') {
  const prompt = `Provide a detailed Chinese zodiac reading for someone born in the Year of the ${animal} (${year}).

Include:
- Personality traits of the ${animal}
- Element associated with ${year}
- Compatible and incompatible animals
- Career prospects and ideal professions
- Love and relationship tendencies
- Health considerations
- Lucky and unlucky aspects (numbers, colors, directions, flowers)
- Current year forecast for ${animal}
- Famous people born in the Year of the ${animal}

Make it comprehensive and culturally respectful.`;
  return generateContent(prompt, language);
}

export async function getPlanetaryTransits(sign, language = 'en') {
  const prompt = `Describe the current major planetary transits and their effects on ${sign}.

Include:
- Current positions of major planets (approximate)
- How each major transit affects ${sign}
- Saturn's transit effect
- Jupiter's transit effect
- Any retrogrades currently happening and their impact
- Eclipse effects if any upcoming
- Best dates/periods in the coming month
- Challenging dates to be aware of
- Overall cosmic weather for ${sign}

Be specific and actionable.`;
  return generateContent(prompt, language);
}

export async function getVedicAstrology(name, dob, timeOfBirth, placeOfBirth, language = 'en') {
  const prompt = `Provide a Vedic astrology (Jyotish) reading for:
Name: ${name}
Date of Birth: ${dob}
Time of Birth: ${timeOfBirth}
Place of Birth: ${placeOfBirth}

Include:
- Rashi (Moon sign) and Nakshatra
- Lagna (Ascendant) analysis
- Current Mahadasha and Antardasha period
- Mangal Dosha analysis
- Sade Sati status (Saturn's transit over Moon)
- Key Yogas in the chart
- Remedies and gemstone recommendations
- Favorable and unfavorable periods ahead
- Spiritual guidance based on the chart

Make it authentic to Vedic astrology traditions.`;
  return generateContent(prompt, language);
}

export async function askAstrologer(question, context = '', language = 'en') {
  const prompt = `${context ? `Context: ${context}\n\n` : ''}The user asks: "${question}"

Provide a thoughtful, detailed astrological answer. If the question relates to specific birth details, use astrological principles to give personalized guidance. If it's a general astrology question, be educational and insightful.`;
  return generateContent(prompt, language);
}

// ===== New API Functions =====

export async function getMoonPhaseReading(moonPhase, tithi, date, language = 'en') {
  const prompt = `Today is ${date}. The Moon is in ${moonPhase} phase, and the Tithi is ${tithi}.

Provide a detailed reading including:
- Spiritual significance of this moon phase
- Significance of ${tithi} in Vedic astrology
- Best activities for this lunar day
- Activities to avoid
- Meditation and spiritual practices recommended
- Mantras or prayers beneficial today
- Effects on different zodiac signs
- Any special observances or festivals

Make it practical and spiritually meaningful.`;
  return generateContent(prompt, language);
}

export async function getMuhuratReading(activityType, startDate, endDate, zodiacSign, language = 'en') {
  const activities = {
    wedding: 'wedding/marriage ceremony (Vivah Muhurat)',
    business: 'starting a new business or signing contracts',
    travel: 'beginning an important journey',
    housewarming: 'housewarming ceremony (Griha Pravesh)',
    vehicle: 'purchasing or taking delivery of a vehicle',
    education: 'starting education or joining school/college',
    namingCeremony: 'naming ceremony (Namkaran)',
    investment: 'making investments or financial decisions',
    medical: 'medical procedures or surgeries',
    spiritual: 'spiritual initiation or religious ceremonies',
  };

  const prompt = `Find auspicious dates (Shubh Muhurat) for ${activities[activityType] || activityType} between ${startDate} and ${endDate}${zodiacSign ? ` for someone with ${zodiacSign} zodiac sign` : ''}.

Include:
- Top 3-5 most auspicious dates with specific time windows
- Why each date is favorable (planetary alignments, tithis, nakshatras)
- Any dates to definitely avoid (Rahukaal, inauspicious yogas)
- Recommended rituals or preparations before the activity
- Lucky colors and directions for each date
- Any special considerations for this type of activity

Be specific with dates and times.`;
  return generateContent(prompt, language);
}

export async function getNakshatraReading(name, dob, timeOfBirth, placeOfBirth, language = 'en') {
  const prompt = `Calculate and provide a Nakshatra (Birth Star) reading for:
Name: ${name || 'Native'}
Date of Birth: ${dob}
Time of Birth: ${timeOfBirth || 'Not provided'}
Place of Birth: ${placeOfBirth || 'Not provided'}

Include:
- Birth Nakshatra (Janma Nakshatra) identification
- Nakshatra Pada (quarter)
- Ruling deity and their blessings
- Planetary lord of the nakshatra
- Physical and personality characteristics
- Mental and emotional tendencies
- Compatible nakshatras for marriage
- Career inclinations based on nakshatra
- Health tendencies
- Auspicious letters for naming
- Lucky colors, numbers, and days
- Spiritual path and life purpose
- Current nakshatra transit effects

Make it detailed and authentic to Jyotish.`;
  return generateContent(prompt, language);
}

export async function getRemediesReading(zodiacSign, category, dosha, concern, language = 'en') {
  const doshaNames = {
    manglik: 'Manglik/Mangal Dosha (Mars affliction)',
    kalsarpa: 'Kalsarpa Dosha (Rahu-Ketu axis)',
    shani: 'Shani Dosha (Saturn affliction)',
    rahu: 'Rahu Dosha',
    ketu: 'Ketu Dosha',
    pitru: 'Pitru Dosha (ancestral karma)',
  };

  const prompt = `Provide Vedic astrological remedies (Upaya) for:
${zodiacSign ? `Zodiac Sign: ${zodiacSign}` : ''}
${dosha ? `Dosha: ${doshaNames[dosha] || dosha}` : ''}
${category ? `Remedy Type Focus: ${category}` : ''}
${concern ? `Specific Concern: ${concern}` : ''}

Include comprehensive remedies:

**Gemstones (Ratna)**:
- Primary recommended gemstone with weight and metal
- How to wear and when to wear
- Activation mantra

**Mantras & Chanting**:
- Specific mantras with count
- Best time for chanting
- Duration of practice

**Fasting (Vrat)**:
- Recommended fasting days
- What to eat and avoid

**Charity (Daan)**:
- Items to donate
- Best days for donation
- To whom to donate

**Yantra**:
- Recommended yantra if applicable
- How to energize and place

**Puja & Rituals**:
- Specific pujas to perform
- Temples to visit

**Lifestyle Changes**:
- Colors to wear/avoid
- Directions favorable/unfavorable
- Daily practices

Make remedies practical and authentic.`;
  return generateContent(prompt, language);
}

export async function getGunaCompatibility(sign1, sign2, name1, name2, dob1, dob2, language = 'en') {
  const prompt = `Provide detailed Ashtakoot Guna Milan (Vedic compatibility analysis) for:

Person 1: ${name1 || 'Person 1'}
- Zodiac Sign: ${sign1}
${dob1 ? `- Date of Birth: ${dob1}` : ''}

Person 2: ${name2 || 'Person 2'}
- Zodiac Sign: ${sign2}
${dob2 ? `- Date of Birth: ${dob2}` : ''}

Calculate and explain all 8 Kutas (36 points system):
1. Varna (1 point) - Spiritual compatibility
2. Vashya (2 points) - Mutual attraction
3. Tara (3 points) - Birth star compatibility
4. Yoni (4 points) - Sexual compatibility
5. Graha Maitri (5 points) - Mental compatibility
6. Gana (6 points) - Temperament match
7. Bhakoot (7 points) - Love and family life
8. Nadi (8 points) - Health and progeny

Also include:
- Total Guna score out of 36
- Interpretation of the score
- Manglik status compatibility
- Major doshas affecting the match
- Remedies for any negative factors
- Overall compatibility verdict
- Advice for the relationship

Make it detailed and follow traditional Jyotish guidelines.`;
  return generateContent(prompt, language);
}

export async function getExtendedTarotReading(cards, spreadType, question, language = 'en') {
  const cardDescriptions = cards.map(c =>
    `${c.position}: ${c.name}${c.reversed ? ' (Reversed)' : ' (Upright)'}`
  ).join('\n');

  const spreadDescriptions = {
    celticCross: 'Celtic Cross (10-card comprehensive spread)',
    loveSpread: 'Love Spread (5-card relationship focus)',
    careerSpread: 'Career Spread (5-card professional guidance)',
    yesNo: 'Single Card Yes/No reading',
    threeCard: 'Three Card Spread (Past/Present/Future)',
  };

  const prompt = `Interpret this ${spreadDescriptions[spreadType] || spreadType} tarot reading:

${cardDescriptions}

${question ? `Question: "${question}"` : 'General life guidance reading'}

For EACH card provide:
- Card meaning in its position${cards[0]?.reversed ? ' (reversed interpretation)' : ''}
- Key symbols and imagery significance
- How it connects to neighboring cards

Then provide:
- Overall theme and narrative
- Key message from the cards
- Timing indicators if any
- Practical action steps
- Spiritual guidance
- Shadow aspects to be aware of

Make it mystical, detailed, and empowering.`;
  return generateContent(prompt, language);
}

// ===== Palm Reading with Image Analysis =====

export async function getPalmReading(imageBase64, palmType, name, gender, language = 'en') {
  const palmLangMap = {
    hi: 'Hindi (हिन्दी) using Devanagari script',
    te: 'Telugu (తెలుగు) using Telugu script',
    ta: 'Tamil (தமிழ்) using Tamil script',
    kn: 'Kannada (ಕನ್ನಡ) using Kannada script',
    ml: 'Malayalam (മലയാളം) using Malayalam script',
  };
  const systemLangInstruction = palmLangMap[language]
    ? `\n\nRespond ONLY in ${palmLangMap[language]}.`
    : '';

  const palmPrompt = `Analyze this ${palmType} hand palm${name ? ` for ${name}` : ''}${gender ? ` (${gender})` : ''}.

Provide palm reading covering:

**MAJOR LINES:**
- Heart Line: emotions, relationships
- Head Line: intellect, career aptitude
- Life Line: vitality, life changes
- Fate Line: career path, success

**MOUNTS & HAND SHAPE:**
- Key mounts visible (Jupiter, Venus, Moon, etc.)
- Hand type and finger characteristics

**OVERALL READING:**
- Personality summary
- Strengths and challenges
- Life guidance
- Lucky colors and numbers

Be encouraging and insightful.`;

  try {
    const response = await axios.post(AI_VISION_URL, {
      model: 'openai/gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: `You are AstroA2Z, an expert palmist.${systemLangInstruction}`
        },
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: palmPrompt
            },
            {
              type: 'image_url',
              image_url: {
                url: imageBase64
              }
            }
          ]
        },
      ],
      max_tokens: 1500,
    });

    return response.data.content || '';
  } catch (err) {
    if (err.response?.status === 429) {
      throw new Error('API rate limit exceeded. Please wait a moment and try again.');
    }
    throw new Error(err.response?.data?.message || err.message);
  }
}

// ===== Kundali / Birth Chart Analysis =====

export async function getKundaliReading(name, dob, timeOfBirth, placeOfBirth, gender, ascendant, planets, language = 'en') {
  const planetInfo = planets.map(p =>
    `${p.name} in ${p.sign} at ${p.degree}°${p.minutes}'${p.seconds}" in ${p.nakshatra} (Pada ${p.pada})${p.retrograde ? ' [Retrograde]' : ''}`
  ).join('\n');

  const prompt = `Generate a comprehensive Kundali (Vedic Birth Chart) reading in the traditional Jyotish style for:

**Birth Details:**
Name: ${name}
Date of Birth: ${dob}
Time of Birth: ${timeOfBirth}
Place of Birth: ${placeOfBirth}
Gender: ${gender}

**Lagna (Ascendant):** ${ascendant}

**Planetary Positions:**
${planetInfo}

Please provide a detailed Kundali analysis including:

**1. LAGNA ANALYSIS (Ascendant):**
- Physical appearance and constitution
- Personality traits and temperament
- Health tendencies
- Overall life direction

**2. GRAHA ANALYSIS (Planetary Study):**
- Sun (Surya) - Soul, father, authority, career
- Moon (Chandra) - Mind, mother, emotions
- Mars (Mangal) - Energy, courage, siblings
- Mercury (Budha) - Intelligence, communication
- Jupiter (Guru) - Wisdom, fortune, spirituality
- Venus (Shukra) - Love, marriage, arts
- Saturn (Shani) - Karma, discipline, longevity
- Rahu - Worldly desires, foreign connections
- Ketu - Spirituality, past karma

**3. BHAVA ANALYSIS (House Significances):**
Analyze key houses and their lords

**4. YOGA ANALYSIS:**
- Identify any Raj Yogas (combinations for success)
- Dhana Yogas (wealth combinations)
- Any Dosh (afflictions) present
- Manglik Dosha analysis if applicable

**5. DASHA ANALYSIS:**
- Current Mahadasha and its effects
- Upcoming Antardasha periods
- Key predictions for current and next dashas

**6. NAKSHATRA ANALYSIS:**
- Janma Nakshatra significance
- Nakshatra lord and deity
- Personality based on birth star

**7. PREDICTIONS & GUIDANCE:**
- Career and profession guidance
- Marriage and relationships
- Health considerations
- Wealth and prosperity
- Spiritual path
- Favorable and unfavorable periods

**8. REMEDIES (Upaya):**
- Gemstones to wear
- Mantras for planets
- Favorable colors, days, directions
- Charity and fasting recommendations
- Temples to visit

Make the reading authentic to Vedic astrology traditions, detailed, and personally meaningful. Use traditional Jyotish terminology while keeping it understandable.`;

  return generateContent(prompt, language);
}
